
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { ProjectPlan, SafetyPoint, InspectionItem } from '../types';
import { Map, ZoomIn, ZoomOut, RotateCcw, MapPin, MousePointer2, Hash, Palette, Plus, Edit2, Trash2, FileText, ChevronDown, Loader2 } from 'lucide-react';
import { selectItemsOnPlan } from '../services/pdf/pdfUtils';
import { pinsAufPlan } from '../services/planPins';
import { nadelNeigungen } from '../services/planNadelNeigung';
import { PlanAnlegen } from './plaene/PlanAnlegen';
import { PlanNadel, nadelAbstandPx } from './plaene/PlanNadel';

interface Props {
  plans: ProjectPlan[];
  points: SafetyPoint[];
  items?: InspectionItem[]; // New: Defects to show on map
  onChange: (points: SafetyPoint[]) => void;
  onPlansChange?: (plans: ProjectPlan[]) => void; // New: Handle plan management
  activeSectionId?: string | null;
  projectId: string;
}

const COLORS = [
    { id: 'green', label: 'Grün', bg: 'bg-emerald-600', ring: 'ring-emerald-600', border: 'border-emerald-600', hex: '#059669' },
    { id: 'blue', label: 'Blau', bg: 'bg-blue-600', ring: 'ring-blue-600', border: 'border-blue-600', hex: '#2563eb' },
    { id: 'yellow', label: 'Gelb', bg: 'bg-yellow-500', ring: 'ring-yellow-500', border: 'border-yellow-500', hex: '#eab308' },
    { id: 'purple', label: 'Lila', bg: 'bg-violet-600', ring: 'ring-violet-600', border: 'border-violet-600', hex: '#7c3aed' },
    { id: 'orange', label: 'Orange', bg: 'bg-orange-500', ring: 'ring-orange-500', border: 'border-orange-500', hex: '#f97316' },
];

/**
 * CSS-Farbe einer Punktfarbe fuer die Nadel: Farbnamen der Palette -> Hexwert,
 * eigene Farben (#rrggbb) direkt. Ohne Farbe gilt Gruen wie bisher.
 */
const punktFarbe = (farbe?: string): string => {
    const wert = farbe || 'green';
    return COLORS.find(c => c.id === wert)?.hex ?? wert;
};

interface BildGroesse { b: number; h: number }

/** Planbild noch nicht gemessen (erster Render, Server-Markup): angenommene Groesse (px), A4 quer. */
const RUECKFALL_BILD: BildGroesse = { b: 1000, h: 1000 / Math.SQRT2 };

/** Gezeichnete Groesse (px, ohne CSS-Transform) eines geladenen Bilds; sonst null. */
const bildGroesseVon = (img: HTMLImageElement | null): BildGroesse | null =>
    img && img.offsetWidth > 0 && img.offsetHeight > 0 ? { b: img.offsetWidth, h: img.offsetHeight } : null;

/** State-Updater: neue Messung uebernehmen, gleiche Groesse ohne neues Objekt (kein Extra-Render). */
const groesseUebernehmen = (neu: BildGroesse | null) => (alt: BildGroesse | null): BildGroesse | null =>
    !neu || (alt && alt.b === neu.b && alt.h === neu.h) ? alt : neu;

const DEFAULT_LABELS = ['Lotsenpunkt', 'Erste Hilfe', 'Feuerlöscher', 'Sammelplatz', 'Gefahrenstelle', 'Baustrom', 'Wasseranschluss'];

// Calculate numbers relative to CURRENT PLAN or SECTION?
// Usually Safety Points are unique per Plan.
const nextNumberForLabel = (points: SafetyPoint[], planId: string | null, label: string) => {
    const numbers = points
      .filter(p => p.planId === planId && p.label.toLowerCase() === label.toLowerCase())
      .map(p => p.number || 0);
    return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
};

/** Feste leere Liste: ein neues `[]` je Render hebelte das Nadel-Memo unten aus. */
const KEINE_EINTRAEGE: InspectionItem[] = [];

export const SafetyPointsView: React.FC<Props> = ({ plans, points, items = KEINE_EINTRAEGE, onChange, onPlansChange, activeSectionId, projectId }) => {
  // STRICT FILTER: Only show plans matching the section
  const visiblePlans = useMemo(() => {
      // If activeSectionId is 'all', show ALL plans.
      // If activeSectionId is specific, ONLY show plans with that ID.
      // If activeSectionId is undefined/null (global project context), show plans with NO sectionId.
      return plans.filter(p => {
          if (!activeSectionId || activeSectionId === 'all') return true; // Loose logic for "All" or if no sections used
          if (p.sectionId === activeSectionId) return true;
          return false;
      });
  }, [plans, activeSectionId]);

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  // Fällt die Auswahl aus dem Abschnitts-Filter (oder wurde noch keine getroffen), greift der erste sichtbare Plan.
  const activePlanId = selectedPlanId && visiblePlans.some(p => p.id === selectedPlanId)
      ? selectedPlanId
      : (visiblePlans[0]?.id ?? null);

  const [scale, setScale] = useState(1);

  // Pin configuration
  const [currentLabel, setCurrentLabel] = useState('Lotsenpunkt');
  const [currentNumber, setCurrentNumber] = useState(() => nextNumberForLabel(points, activePlanId, 'Lotsenpunkt'));
  const [currentColor, setCurrentColor] = useState('green');

  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);

  // Plan Management States
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [tempPlanName, setTempPlanName] = useState('');

  const planImageRef = useRef<HTMLImageElement>(null);

  const activePlan = plans.find(p => p.id === activePlanId);
  const activePoints = points.filter(p => p.planId === activePlanId);
  
  // Filter defects for this plan
  // Dieselbe Funktion wie der Plananhang im PDF: „Information" und „Erledigt"
  // behalten beim Statuswechsel ihre Koordinaten (verlustfrei), dürfen aber
  // nirgends mehr als Punkt erscheinen — sonst zeigen Bildschirm und Ausdruck
  // unterschiedliche Pläne.
  // Note: Items are already filtered by parent InspectionList if using activeSectionId,
  // but the status/plan filter here ensures no cross-pollination.
  // (Angewandt unten im useMemo der Nadeln.)

  // Gezeichnete Groesse des Planbilds (ohne den Zoom-Transform), damit nahe
  // Nadeln in Bildschirm-Pixeln aufgefaechert werden koennen.
  const [bildGroesse, setBildGroesse] = useState<BildGroesse | null>(null);
  const hatPlan = !!activePlan;
  useEffect(() => {
      const img = planImageRef.current;
      if (!img || typeof ResizeObserver === 'undefined') return undefined;
      // Meldet sich beim Anmelden einmal von selbst und nach jedem Bildwechsel mit neuer Groesse.
      const beobachter = new ResizeObserver(() => setBildGroesse(groesseUebernehmen(bildGroesseVon(img))));
      beobachter.observe(img);
      return () => beobachter.disconnect();
  }, [hatPlan]);

  // Alle Nadeln des Plans (Maengel blass + Sicherheitspunkte) gemeinsam:
  // naeher als nadelAbstandPx auf dem Bildschirm → faechern auf (Betreiber
  // 28.09.: „drehe sie leicht weg“). Die Nadeln bleiben per gegenSkala gleich
  // gross, der Plan waechst mit `scale` — daher Bildgroesse × scale.
  // Nur neu rechnen, wenn sich Nadeln, Plan, Bildgroesse oder Zoom aendern —
  // nicht bei jeder Eingabe in der Seitenleiste (Codex-Review 28.09.).
  const { maengelNadeln, neigungen } = useMemo(() => {
      const nadeln = selectItemsOnPlan(items, activePlanId)
          .flatMap(d => pinsAufPlan(d, activePlanId).map(pin => ({ d, pin })));
      const punkte = points.filter(p => p.planId === activePlanId);
      const bild = bildGroesse ?? RUECKFALL_BILD;
      return {
          maengelNadeln: nadeln,
          neigungen: nadelNeigungen(
              [...nadeln.map(({ pin }) => pin), ...punkte].map(p => ({
                  x: (p.x / 100) * bild.b * scale,
                  y: (p.y / 100) * bild.h * scale,
              })),
              nadelAbstandPx('normal'),
          ),
      };
  }, [items, points, activePlanId, bildGroesse, scale]);
  const punktNeigung = (i: number) => neigungen[maengelNadeln.length + i];

  const availableLabels = useMemo(() => {
      const usedLabels = new Set(points.map(p => p.label));
      return Array.from(new Set([...DEFAULT_LABELS, ...usedLabels])).sort();
  }, [points]);

  const getNextNumberForLabel = (label: string) => nextNumberForLabel(points, activePlanId, label);

  // Re-calc when plan changes
  const [numberedPlanId, setNumberedPlanId] = useState<string | null>(activePlanId);
  if (numberedPlanId !== activePlanId) {
      setNumberedPlanId(activePlanId);
      setCurrentNumber(getNextNumberForLabel(currentLabel));
  }

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newLabel = e.target.value;
      setCurrentLabel(newLabel);
      setShowAllOptions(false); 
      setIsDropdownOpen(true);
      setCurrentNumber(getNextNumberForLabel(newLabel));
  };

  const selectLabel = (lbl: string) => {
      setCurrentLabel(lbl);
      setCurrentNumber(getNextNumberForLabel(lbl));
      setIsDropdownOpen(false);
  };

  const handlePlanClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!activePlanId || !planImageRef.current) return;
    
    const rect = planImageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const newPoint: SafetyPoint = {
        id: crypto.randomUUID(),
        planId: activePlanId,
        x, 
        y,
        label: currentLabel,
        number: currentNumber,
        color: currentColor
    };
    
    const newPoints = [...points, newPoint];
    onChange(newPoints);
    setCurrentNumber(prev => prev + 1);
  };

  // Die Nadel stoppt den Klick selbst (PlanNadel) — sonst setzte der Plan
  // darunter gleich wieder einen neuen Punkt.
  const removePoint = (id: string) => {
      onChange(points.filter(p => p.id !== id));
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = -Math.sign(e.deltaY) * 0.1;
        setScale(prev => Math.min(Math.max(0.5, prev + delta), 4));
    }
  };

  // Neuer Plan kommt fertig zugeschnitten, benannt und hochgeladen aus
  // PlanAnlegen. Ohne onPlansChange gibt es keinen Ausloeser (siehe unten).
  // Der Abschnitt wird wie bisher aus dem aktiven Abschnitt uebernommen.
  const abschnittFuerNeuenPlan = (activeSectionId && activeSectionId !== 'all') ? activeSectionId : undefined;
  const planAngelegt = (plan: ProjectPlan) => {
      if (!onPlansChange) return;
      onPlansChange([...plans, plan]);
      setSelectedPlanId(plan.id);
  };

  const removePlan = (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!onPlansChange || !window.confirm("Plan wirklich löschen?")) return;
      
      const updated = plans.filter(p => p.id !== id);
      onPlansChange(updated);
      
      if (activePlanId === id) {
          // Re-calculate visible plans
          const remainingVisible = updated.filter(p => !activeSectionId || activeSectionId === 'all' || !p.sectionId || p.sectionId === activeSectionId);
          setSelectedPlanId(remainingVisible.length > 0 ? remainingVisible[0].id : null);
      }
  };

  const startEditingPlan = (plan: ProjectPlan, e: React.MouseEvent) => {
      e.stopPropagation();
      setEditingPlanId(plan.id);
      setTempPlanName(plan.name);
  };

  const saveEditedPlan = () => {
      if (editingPlanId && tempPlanName.trim() && onPlansChange) {
          const updatedPlans = plans.map(p => 
              p.id === editingPlanId ? { ...p, name: tempPlanName.trim() } : p
          );
          onPlansChange(updatedPlans);
          setEditingPlanId(null);
          setTempPlanName('');
      }
  };

  const filteredLabels = showAllOptions 
      ? availableLabels 
      : availableLabels.filter(lbl => lbl.toLowerCase().includes(currentLabel.toLowerCase()));

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] animate-fade-in relative">
        
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-4 flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
            <div>
                <h2 className="text-lg font-bold text-blue-800 flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                    Sicherheits- & Lotsenplan
                </h2>
                <p className="text-xs text-slate-500">
                    Planverwaltung und Markierungen für Erste Hilfe, Lotsenpunkte oder Feuerlöscher.
                </p>
            </div>
            
            <div className="flex items-center gap-2">
                <div className="flex items-center bg-slate-100 rounded-lg p-1">
                    <button onClick={() => setScale(s => Math.max(0.5, s - 0.25))} className="p-2 hover:bg-slate-200 rounded text-slate-600"><ZoomOut className="w-4 h-4"/></button>
                    <span className="w-12 text-center text-xs font-mono text-slate-600">{Math.round(scale * 100)}%</span>
                    <button onClick={() => setScale(s => Math.min(4, s + 0.25))} className="p-2 hover:bg-slate-200 rounded text-slate-600"><ZoomIn className="w-4 h-4"/></button>
                    <button onClick={() => setScale(1)} className="p-2 hover:bg-slate-200 rounded text-slate-600 ml-1 border-l border-slate-200"><RotateCcw className="w-4 h-4"/></button>
                </div>
            </div>
        </div>

        <div className="flex flex-1 min-h-0 gap-6">
            {/* Toolbar Sidebar */}
            <div className="w-72 bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col gap-6 overflow-y-auto shrink-0">
                
                {/* Plan Management Section */}
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Projektpläne</label>
                        {/* Zweiter Ausloeser (Plan anlegen) — ohne Callback ebenfalls weg. */}
                        {onPlansChange && (
                            <PlanAnlegen
                                projectId={projectId}
                                sectionId={abschnittFuerNeuenPlan}
                                anzahlVorhanden={plans.length}
                                onAngelegt={planAngelegt}
                            >
                                {(oeffnen, laedt) => (
                                    <button
                                        type="button"
                                        onClick={oeffnen}
                                        disabled={laedt}
                                        className="text-blue-600 hover:bg-blue-50 p-1 rounded transition-colors disabled:opacity-50"
                                        title="Plan hinzufügen"
                                        aria-label="Plan hinzufügen"
                                    >
                                        {laedt ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}
                                    </button>
                                )}
                            </PlanAnlegen>
                        )}
                    </div>
                    
                    <div className="space-y-2 mb-3 max-h-[200px] overflow-y-auto pr-1">
                        {visiblePlans.length === 0 ? (
                            <p className="text-xs text-slate-400 italic">
                                {plans.length > 0 ? "Keine Pläne für diesen Abschnitt." : "Keine Pläne vorhanden."}
                            </p>
                        ) : (
                            visiblePlans.map(p => (
                                <div 
                                    key={p.id} 
                                    onClick={() => { setSelectedPlanId(p.id); setScale(1); }}
                                    className={`group flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-all ${activePlanId === p.id ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200' : 'bg-white border-slate-100 hover:border-slate-300'}`}
                                >
                                    <div className="flex items-center gap-2 overflow-hidden flex-1">
                                        <FileText className={`w-4 h-4 shrink-0 ${activePlanId === p.id ? 'text-blue-500' : 'text-slate-400'}`} />
                                        {editingPlanId === p.id ? (
                                            <input 
                                                type="text" 
                                                value={tempPlanName}
                                                onChange={(e) => setTempPlanName(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-full text-xs px-1 py-0.5 border rounded outline-none"
                                                autoFocus
                                                onKeyDown={(e) => e.key === 'Enter' && saveEditedPlan()}
                                                onBlur={saveEditedPlan}
                                            />
                                        ) : (
                                            <span className={`text-xs font-medium truncate ${activePlanId === p.id ? 'text-blue-700' : 'text-slate-600'}`}>{p.name}</span>
                                        )}
                                    </div>
                                    
                                    {onPlansChange && (
                                        <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity">
                                            <button 
                                                onClick={(e) => startEditingPlan(p, e)}
                                                className="p-1 hover:bg-white rounded text-slate-400 hover:text-blue-500"
                                            >
                                                <Edit2 className="w-3 h-3"/>
                                            </button>
                                            <button 
                                                onClick={(e) => removePlan(p.id, e)}
                                                className="p-1 hover:bg-white rounded text-slate-400 hover:text-red-500"
                                            >
                                                <Trash2 className="w-3 h-3"/>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                    
                    {/*
                      * Nur mit Callback: ohne ihn liesse sich der Dateidialog zwar
                      * oeffnen, hochgeladen wuerde aber nichts. Die vorhandenen
                      * Plaene bleiben oben unveraendert sichtbar.
                      */}
                    {onPlansChange && (
                        <PlanAnlegen
                            projectId={projectId}
                            sectionId={abschnittFuerNeuenPlan}
                            anzahlVorhanden={plans.length}
                            onAngelegt={planAngelegt}
                            className="w-full text-xs py-2 h-auto"
                        />
                    )}
                </div>

                <hr className="border-slate-100" />

                {/* Point Settings */}
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">Bezeichnung</label>
                    <div className="relative">
                        <input 
                            type="text" 
                            value={currentLabel}
                            onChange={handleLabelChange}
                            onFocus={() => { setIsDropdownOpen(true); setShowAllOptions(false); }}
                            onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
                            className="w-full pl-3 pr-8 py-2 border border-slate-300 rounded-lg text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-colors"
                            placeholder="z.B. Lotsenpunkt"
                        />
                        <button 
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-blue-600 transition-colors"
                            onMouseDown={(e) => {
                                e.preventDefault(); // Prevents blur on input
                                setIsDropdownOpen(!isDropdownOpen);
                                setShowAllOptions(true);
                            }}
                            tabIndex={-1}
                        >
                            <ChevronDown className="w-4 h-4" />
                        </button>
                        
                        {isDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto animate-fade-in">
                                {filteredLabels.length > 0 ? (
                                    filteredLabels.map(lbl => {
                                        const count = points.filter(p => p.label === lbl).length;
                                        return (
                                            <div 
                                                key={lbl} 
                                                onMouseDown={() => selectLabel(lbl)}
                                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-slate-700 border-b border-slate-50 last:border-0 flex justify-between items-center group"
                                            >
                                                <span>{lbl}</span>
                                                {count > 0 && (
                                                    <span className="text-[11px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full group-hover:bg-blue-100 group-hover:text-blue-600">
                                                        {count}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="px-3 py-2 text-xs text-slate-400 italic text-center">Keine Vorschläge</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">Nächste Nummer</label>
                    <div className="relative">
                        <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input 
                            type="number"
                            min="1" 
                            value={currentNumber}
                            onChange={(e) => setCurrentNumber(parseInt(e.target.value) || 1)}
                            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-colors font-mono font-bold"
                        />
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">Zählt für "{currentLabel}" automatisch hoch.</p>
                </div>

                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">Farbe</label>
                    <div className="grid grid-cols-3 gap-2">
                        {COLORS.map(c => (
                            <button
                                key={c.id}
                                onClick={() => setCurrentColor(c.id)}
                                className={`w-full aspect-square rounded-lg flex items-center justify-center transition-all border-2 ${currentColor === c.id ? `border-slate-800 scale-110 shadow-md` : 'border-transparent hover:scale-105'}`}
                                title={c.label}
                            >
                                <div className={`w-6 h-6 rounded-full ${c.bg} shadow-inner`}></div>
                            </button>
                        ))}
                        <label 
                            className={`w-full aspect-square rounded-lg flex items-center justify-center transition-all border-2 cursor-pointer bg-slate-50 hover:bg-slate-100 ${!COLORS.find(c => c.id === currentColor) ? 'border-slate-800 scale-110 shadow-md' : 'border-transparent hover:scale-105'}`}
                            title="Eigene Farbe wählen"
                        >
                            <input 
                                type="color" 
                                className="w-0 h-0 opacity-0 absolute"
                                value={currentColor.startsWith('#') ? currentColor : '#000000'}
                                onChange={(e) => setCurrentColor(e.target.value)}
                            />
                            <div className="flex flex-col items-center">
                                <div 
                                    className="w-6 h-6 rounded-full shadow-inner border border-slate-200 flex items-center justify-center overflow-hidden"
                                    style={{ background: currentColor.startsWith('#') ? currentColor : 'conic-gradient(from 180deg at 50% 50%, #FF0000 0deg, #00FF00 120deg, #0000FF 240deg, #FF0000 360deg)' }}
                                >
                                    {!currentColor.startsWith('#') && <Palette className="w-3 h-3 text-white drop-shadow-md" />}
                                </div>
                            </div>
                        </label>
                    </div>
                </div>

                {/* Preview Box: dieselbe Nadel wie auf dem Plan (Spitze auf dem Punkt).
                    `isolate`: die Nadel stapelt per zIndex — nicht ueber die Bezeichnungs-Liste. */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex flex-col items-center">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Vorschau</label>
                    <div className="relative isolate w-full h-20">
                        <PlanNadel
                            x={50}
                            y={92}
                            nummer={currentNumber}
                            beschriftung={currentLabel}
                            farbe={punktFarbe(currentColor)}
                        />
                    </div>
                </div>

                <div className="mt-auto pt-4 border-t border-slate-100">
                    <div className="bg-blue-50 text-blue-700 p-3 rounded-lg text-xs flex gap-2 items-start">
                        <MousePointer2 className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>Klicken Sie auf den Plan, um den Punkt zu setzen. Zum Löschen den Punkt antippen.</span>
                    </div>
                </div>
            </div>

            {/* Plan Area */}
            <div className="flex-1 bg-slate-900 rounded-xl overflow-hidden relative flex items-center justify-center cursor-crosshair shadow-inner" onWheel={handleWheel}>
                 {activePlan ? (
                     <div
                        className="relative isolate transition-transform duration-100 ease-out origin-center"
                        style={{ transform: `scale(${scale})` }}
                        onClick={handlePlanClick}
                     >
                         <img
                            ref={planImageRef}
                            src={activePlan.image}
                            alt="Plan"
                            className="max-w-none block bg-white shadow-2xl"
                            draggable={false}
                            onLoad={(e) => setBildGroesse(groesseUebernehmen(bildGroesseVon(e.currentTarget)))}
                         />

                         {/* Mängel-Nadeln (nur Anzeige): dieselben Nadeln wie im PDF-Plananhang
                             (selectItemsOnPlan + pinsAufPlan) — ein Mangel kann mehrere tragen. */}
                         {maengelNadeln.map(({ d, pin }, i) => (
                             <PlanNadel
                                key={`${d.id}-${pin.index}`}
                                x={pin.x}
                                y={pin.y}
                                neigung={neigungen[i]}
                                nummer={d.ticketNumber}
                                blass
                                gegenSkala={scale}
                                groesse="klein"
                             />
                         ))}

                         {activePoints.map((p, i) => (
                             <PlanNadel
                                key={p.id}
                                x={p.x}
                                y={p.y}
                                neigung={punktNeigung(i)}
                                nummer={p.number}
                                beschriftung={p.label}
                                farbe={punktFarbe(p.color)}
                                gegenSkala={scale}
                                onClick={() => removePoint(p.id)}
                                entfernbar
                                title="Antippen zum Entfernen"
                             />
                         ))}
                     </div>
                 ) : (
                     <div className="flex flex-col items-center justify-center h-full text-white opacity-50">
                         <Map className="w-16 h-16 mb-4 opacity-50" />
                         <p>
                             {visiblePlans.length === 0 ? "Keine Pläne für diesen Abschnitt." : "Bitte Plan aus der Liste wählen."}
                         </p>
                     </div>
                 )}
            </div>
        </div>
    </div>
  );
};
